// SPDX-License-Identifier: GPL-3.0-or-later
// The optional engine seam for table rules that run a hold and extra balls.
// A rules object that has the 3 methods below gets the extra engine calls.
// A rules object that does not have them plays as before.
// The engine never imports `src/table`. It reads the rules through this shape only.

import type { TableRules } from "../../shared/contracts";

/** The most extra balls that the engine adds to one game. */
export const MAX_EXTRA_BALLS = 2;

/** The optional methods of a rules object. */
export interface RulesExtension {
  /** True while the rules hold play. The engine runs no physics step then. */
  holdActive(): boolean;
  /** Game time that passes while play is held. It is the `dt` of `step`. */
  onHoldTick(dtMs: number): void;
  /** The extra balls that the player collected in this game. */
  extraBallsEarned(): number;
}

/** The extension of the rules, or null. It checks the 3 methods with typeof. */
export function rulesExtension(rules: TableRules): RulesExtension | null {
  const maybe = rules as Partial<Record<keyof RulesExtension, unknown>>;
  if (
    typeof maybe.holdActive === "function" &&
    typeof maybe.onHoldTick === "function" &&
    typeof maybe.extraBallsEarned === "function"
  ) {
    return rules as TableRules & RulesExtension;
  }
  return null;
}

/** The number of extra balls to add: a whole number from 0 to MAX_EXTRA_BALLS. */
export function clampExtraBalls(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_EXTRA_BALLS, Math.max(0, Math.floor(value)));
}
